import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { FacilityPicker } from "./FacilityPicker";

const ksh = (n) => `KSh ${Math.round(n || 0).toLocaleString()}`;

/**
 * The facility model, attached to a county scenario. Someone picks counties, then sees what one facility would
 * cost and earn in each of them, side by side.
 */
export const ShortlistFacilities = ({ counties = [] }) => {
  const [facility, setFacility] = useState("branded_five");
  const [funding, setFunding] = useState("ppp");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    api.get("/facility-library")
      .then((r) => setLibrary(r.data.facilities || []))
      .catch(() => setLibrary([]));
  }, []);

  useEffect(() => {
    if (!counties.length) {
      setRows([]);
      return;
    }
    setBusy(true);
    Promise.all(
      counties.slice(0, 20).map((c) =>
        api.get(`/facility-model/${facility}?county=${encodeURIComponent(c)}&funding=${funding}`)
          .then((r) => ({ county: c, ...r.data }))
          .catch(() => null),
      ),
    )
      .then((res) => setRows(res.filter(Boolean)))
      .finally(() => setBusy(false));
  }, [counties, facility, funding]);

  if (!counties.length) return null;

  const total = rows.reduce(
    (acc, r) => ({
      capex: acc.capex + r.result.capex_usd,
      ebitda: acc.ebitda + r.result.ebitda_ksh,
      land: acc.land + r.land.land_m2,
    }),
    { capex: 0, ebitda: 0, land: 0 },
  );

  const select = "border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";

  return (
    <section className="mt-16" data-testid="shortlist-facilities">
      <p className="eyebrow">The site, and the numbers</p>
      <h2 className="display-2 mt-4 max-w-4xl">One Facility, Across Your Shortlist</h2>
      <p className="body-copy mt-5 max-w-3xl">
        Pick what you would build and how it is funded, and the model prices it in each county you have selected —
        on that county&rsquo;s own living wage, rent and tariffs. Every facility we model is offered here, labelled
        with what it is for, the land it needs and an indicative build cost.
      </p>

      <div className="mt-7 flex flex-wrap gap-4">
        <label className="block">
          <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Funding route</span>
          <select value={funding} onChange={(e) => setFunding(e.target.value)} data-testid="shortlist-funding-select"
            className={`mt-2 ${select}`}>
            <option value="ppp">Public-private partnership</option>
            <option value="private">Private</option>
            <option value="public">Public</option>
            <option value="diaspora">Diaspora or blended</option>
          </select>
        </label>
        <label className="block">
          <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Facility</span>
          <select value={facility} onChange={(e) => setFacility(e.target.value)} data-testid="shortlist-facility-select"
            className={`mt-2 ${select}`}>
            {library.map((f) => <option key={f.id} value={f.id}>{`${f.name} — ${f.tier_label}`}</option>)}
          </select>
        </label>
      </div>

      <p className="mono mt-8 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
        All {library.length} facility types · tap one to price it across your shortlist
      </p>
      <div className="mt-4">
        <FacilityPicker facilities={library} value={facility} onChange={setFacility} testId="shortlist-facility-picker" />
      </div>

      {rows.length ? (
        <>
          <dl className="mono mt-7 grid gap-3 sm:grid-cols-3" data-testid="shortlist-facility-totals">
            {[
              ["Total build cost", `$${Math.round(total.capex).toLocaleString()}`],
              ["Combined EBITDA, year", ksh(total.ebitda)],
              ["Land required", `${Math.round(total.land).toLocaleString()} m²`],
            ].map(([k, v]) => (
              <div key={k} className="border border-zinc-900 bg-[#0b0b0b] p-5">
                <dt className="text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</dt>
                <dd className="mt-2 font-display text-xl text-white">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[760px] text-left" data-testid="shortlist-facility-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["County", "Build", "Net income", "EBITDA", "Yrs to match cost", "Surplus share", "Land"].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.county} className="border-b border-zinc-900" data-testid={`shortlist-facility-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <td className="px-4 py-3 text-sm text-white">{r.county}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-300">${r.result.capex_usd.toLocaleString()}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{ksh(r.result.net_revenue_ksh)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-300">{ksh(r.result.ebitda_ksh)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{r.result.payback_years ? `${r.result.payback_years} yrs` : "—"}</td>
                    <td className="mono px-4 py-3 text-xs text-ochre">{r.result.roce_pct != null ? `${r.result.roce_pct}%` : "—"}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-500">{r.land.land_m2.toLocaleString()} m²</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mono mt-4 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
            KSKF model output. Excludes land purchase, gate and broadcast income. First twenty counties of a longer
            shortlist are shown. <Link to="/facility-planner" className="text-ochre">Full facility planner</Link>
          </p>
        </>
      ) : busy ? (
        <p className="mono mt-7 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">Pricing your shortlist…</p>
      ) : null}
    </section>
  );
};
